import type { IconName } from "lucide-react/dynamic";

import {
  type Icon,
  PublishedNavigationElementTypeEnum,
  type PublishedNavigationElementTypeEnum as NavigationElementType,
} from "@/lib/api/types";

/**
 * Navigation element `type` values (backend `type_key`). Only the published spec declares
 * the enum; the CMS `NavigationElement.type` is a plain string with the same values.
 */
export const NAVIGATION_ELEMENT_KIND = {
  folder: PublishedNavigationElementTypeEnum.Folder,
  page: PublishedNavigationElementTypeEnum.Page,
  /** Risk-picture page (`/land-warnings` content). */
  publicWarningsPage: PublishedNavigationElementTypeEnum.PublicWarningsPage,
  /** Maritime warnings page (coastal / North Sea map). */
  maritimeWarningsPage: PublishedNavigationElementTypeEnum.MaritimeWarningsPage,
} as const satisfies Record<string, NavigationElementType>;

/**
 * `type` of an element, or undefined when absent. Accepts any object so callers can pass
 * partial elements (published `children` have an optional `type`).
 */
export function navigationElementKind(element: object | undefined): string | undefined {
  if (!element || !("type" in element)) return undefined;
  return typeof element.type === "string" ? element.type : undefined;
}

const BUILT_IN_PAGE_KINDS: readonly string[] = [
  NAVIGATION_ELEMENT_KIND.publicWarningsPage,
  NAVIGATION_ELEMENT_KIND.maritimeWarningsPage,
];

/** Built-in pages have no CMS `page_id`, cannot be nested, and only edit their title. */
export function isBuiltInPageKind(kind: string | undefined): boolean {
  return kind !== undefined && BUILT_IN_PAGE_KINDS.includes(kind);
}

/** Folders are the only droppable navigation elements. */
export function isFolderNavigationElement(element: { type?: string }): boolean {
  return navigationElementKind(element) === NAVIGATION_ELEMENT_KIND.folder;
}

/** Top-level only: built-in pages cannot be nested in a folder. */
export function findNavigationElementByKind<T extends object>(
  elements: T[] | undefined,
  kind: string,
): T | undefined {
  return elements?.find((element) => navigationElementKind(element) === kind);
}

/** Default icon of the built-in warning pages (lucide "triangle-alert"). */
export const WARNING_PAGE_ICON = "triangle-alert" satisfies IconName;

/**
 * Icon to show for a navigation element: its own `icon`, else the warning triangle for
 * built-in warning pages. Undefined lets callers keep their own fallback (file/folder).
 */
export function navigationElementIcon(element: {
  type?: string;
  icon?: Icon;
}): Icon | IconName | undefined {
  if (element.icon) return element.icon;
  return isBuiltInPageKind(navigationElementKind(element)) ? WARNING_PAGE_ICON : undefined;
}

/** "Show page" switch per built-in warning page; `undefined` while settings are loading. */
export interface WarningPageVisibility {
  publicWarningsPage: boolean | undefined;
  maritimeWarningsPage: boolean | undefined;
}

/**
 * Drops built-in warning pages switched off in their settings. Unknown visibility keeps
 * the page, so a failed settings request never hides it. Other kinds always stay.
 * Needed in admin: only the published navigation omits hidden elements.
 */
export function withoutHiddenWarningPages<T extends object>(
  elements: T[],
  visibility: WarningPageVisibility,
): T[] {
  return elements.filter((element) => {
    const kind = navigationElementKind(element);
    if (kind === NAVIGATION_ELEMENT_KIND.publicWarningsPage) {
      return visibility.publicWarningsPage !== false;
    }
    if (kind === NAVIGATION_ELEMENT_KIND.maritimeWarningsPage) {
      return visibility.maritimeWarningsPage !== false;
    }
    return true;
  });
}
